import { doorClient, resolve, ImResponse } from '.';

export const extractFailCauseFromLastMsgContent = (
  talk: Conversation.Conversation,
): Conversation.Conversation => {
  // 后端给了个坑底的数据格式，把错误原因放在了 lastMsgContent中，而lastMsgContent是个string，要先转成json才能拿到
  // 在请求的时候做个转换，方便前端使用。

  if (!talk.lastMsgContent) {
    return talk;
  }

  try {
    const lastMsgContent = JSON.parse(talk.lastMsgContent);
    return {
      ...talk,
      failCause: lastMsgContent.failCause,
    };
  } catch (error) {
    return {
      ...talk,
      failCause: undefined,
    };
  }
};

export enum ConversationListReplied {
  // 未处理
  Untreated = '0',
  // 已处理
  Processed = '1',
}

/** 查询会话列表 */
export function queryConversationList(params: ApiConversation.ConversationListParams) {
  return resolve(
    doorClient.get<ImResponse<ApiConversation.ConversationListResponse>>(
      '/oversea-conversation/v1/queryConversationList',
      {
        params,
        transformResponse(data) {
          try {
            const json = JSON.parse(data);
            return {
              ...json,
              data: {
                ...json.data,
                talkInfo: json.data.talkInfo.map(extractFailCauseFromLastMsgContent),
              },
            };
          } catch (error) {
            return data;
          }
        },
      },
    ),
  );
}

export function queryRemindConversationList(
  params: Omit<ApiConversation.ConversationListParams, 'offset' | 'pageSize'>,
) {
  return resolve(
    doorClient.get<ImResponse<ApiConversation.ConversationListResponse>>(
      '/oversea-conversation/v1/queryRemindConversationList',
      {
        params,
        transformResponse(data) {
          try {
            const json = JSON.parse(data);
            return {
              ...json,
              data: {
                ...json.data,
                talkInfo: json.data.talkInfo.map(extractFailCauseFromLastMsgContent),
              },
            };
          } catch (error) {
            return data;
          }
        },
      },
    ),
  );
}

/** 查询单个会话, 推来消息,本地无会话时调用 */
export function getTalkInfo(params: ApiConversation.GetTalkInfoParam) {
  return resolve(
    doorClient.get<ImResponse<Conversation.Conversation>>('/aggregation/v1/getTalkInfo', {
      params,
      transformResponse(data) {
        try {
          const json = JSON.parse(data);
          return {
            ...json,
            data: extractFailCauseFromLastMsgContent(json.data),
          };
        } catch (error) {
          return data;
        }
      },
    }),
  );
}

export function getTalkInfoWithPhone(
  params: ApiConversation.GetTalkInfoParam & { needPhone?: boolean },
) {
  return resolve(
    doorClient.get<ImResponse<Conversation.Conversation>>('/aggregation/v1/getAggTalk', {
      params,
      transformResponse(data) {
        try {
          const json = JSON.parse(data);
          return {
            ...json,
            data: {
              ...extractFailCauseFromLastMsgContent(json.data.talk),
              phone: json.data.phone,
            },
          };
        } catch (error) {
          return data;
        }
      },
    }),
  );
}

/** 会话转发 */
export function transferConversation(params: ApiConversation.TransferConversationParam) {
  return resolve(
    doorClient.post<ImResponse<undefined>>('/oversea-conversation/v1/transferConversation', params),
  );
}

/** 批量转发会话 */
export function batchTransferConversation(
  payload: ApiConversation.BatchTransferConversationPayload,
) {
  return resolve(
    doorClient.post<ImResponse<undefined>>('/oversea-conversation/v1/batchTransfer', payload),
  );
}

/** 更新会话 */
export function updateConversation(params: ApiConversation.UpdateConversationParam) {
  return resolve(
    doorClient.post<ImResponse<undefined>>('/oversea-conversation/v1/updateConversation', params),
  );
}

export function updateConversationV2(params: ApiConversation.UpdateConversationV2Param) {
  return resolve(
    doorClient.post<ImResponse<ApiConversation.UpdateConversationResData>>(
      '/oversea-conversation/v1/updateConversationV2',
      params,
    ),
  );
}

/** 不在乐聊回复的消息同步,每隔2分钟调用 */
export function syncMessage(params: ApiConversation.SyncMessageParam) {
  return resolve(
    doorClient.post<ImResponse<undefined>>('/oversea-conversation/v1/syncMessage', params),
  );
}

/** 获取店铺列表信息 */
export function getStoreList() {
  return resolve(
    doorClient.get<ImResponse<ApiConversation.StoreListResponse>>(
      '/oversea-conversation/v1/getStoreList',
      {},
    ),
  );
}

/** 查询接待统计信息 */
export function queryReceptionBuyerStat(
  params: ApiConversation.ReceptionBuyerStatParam,
  signal?: AbortSignal,
) {
  return resolve(
    doorClient.get<ImResponse<Conversation.ReceptionBuyerStat>>(
      '/oversea-conversation/v1/queryReceptionBuyerStat',
      { params, signal },
    ),
  );
}

/** 本地搜索: message同步 https://leyan.yuque.com/czpqnb/yzt2rg/awac1v */
export function pullMessage(params: ApiConversation.PullMessageParam) {
  return resolve(
    doorClient.post<ImResponse<ApiConversation.PullMessageResponse>>(
      '/oversea-conversation/v1/pullMessage',
      params,
    ),
  );
}

export interface ISearchKeyword {
  type: 'all' | 'buyer' | 'order' | 'track' | 'item' | 'msg' | 'trade_buyer_id';
  keyword: string;
  offset?: number | string;
  pageSize: number;

  // 支持 type 为 order
  externalStoreId?: string;
  region?: General.UpperCaseRegion;
  tunnelType?: General.PlatformUpperCaseTunnel;

  /** 可以查到超管会话 */
  suTalkMode?: boolean;
}

export interface ISearchResult {
  buyerId: string;
  buyerNick: string;
  conversationId: string;
  externalBuyerId: string;
  externalStoreId: string;
  keyword: string;
  lastMsgId: string;
  lastMsgContent: string;
  lastMsgTime: string;
  lastMsgType: string;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  storeAlias: string;
  storeId: string;
  storeName: string;
  hasTalk: boolean;
  yunXinTid: string;
  tunnelAccountId: string;
  targetMsgContent: string;
  targetMsgId: string;
  targetMsgTime: string;
  targetMsgType: string;
  tradeId: string;
  mockExternal: boolean;
  isSellerAffiliateChat: boolean;
}

export interface ISearchKeywordRst {
  buyerSearchResult: {
    hasNext: boolean;
    searchRow: ISearchResult[];
  };
  orderSearchResult: {
    hasNext: boolean;
    searchRow: ISearchResult[];
  };
}

export function searchKeyword(params: ISearchKeyword) {
  return resolve(
    doorClient.get<ImResponse<ISearchKeywordRst>>('/oversea-conversation/v1/search/searchKeyword', {
      params,
    }),
  );
}

export interface ISearchKeywordResult {
  hasNext: boolean;
  searchRow: ISearchResult[];
  nextOffset: string;
}

export interface ISearchAllResult {
  buyerSearchResult: ISearchKeywordResult;
  orderSearchResult: ISearchKeywordResult;
  trackSearchResult: ISearchKeywordResult;
  itemSearchResult: ISearchKeywordResult;
  msgSearchResult: ISearchKeywordResult;
}

export function searchAll(params: Omit<ISearchKeyword, 'type'>) {
  return resolve(
    doorClient.get<ImResponse<ISearchAllResult>>('/oversea-conversation/v1/search/searchAll', {
      params,
    }),
  );
}

export function searchType(params: ISearchKeyword) {
  return resolve(
    doorClient.get<ImResponse<ISearchKeywordResult>>('/oversea-conversation/v1/search/searchType', {
      params,
    }),
  );
}

export function batchSearch(params: Partial<ISearchKeyword>) {
  return resolve(
    doorClient.get<ImResponse<Pick<ISearchKeywordResult, 'searchRow'>>>(
      '/oversea-conversation/v1/search/batchSearchOrder',
      { params },
    ),
  );
}

/**
 * 根据超管会话id查询会话列表信息，返回会话列表信息和电话信息
 */
export const getConversationListByIds = (
  conversationIds: (number | string)[],
  signal?: AbortSignal,
) => {
  return resolve(
    doorClient
      .post<
        ImResponse<{
          talkInfo: Conversation.Conversation[];
        }>
      >(
        '/aggregation/v1/getSuTalkInfoList',
        {
          conversationIds,
        },
        { signal },
      )
      .then((response) => {
        if (response.data?.data?.talkInfo?.length > 0) {
          response.data.data.talkInfo = response.data.data.talkInfo.map(
            extractFailCauseFromLastMsgContent,
          );
        }
        return response;
      }),
  );
};

// 根据 msgID 查询消息信息
export const getMsgById = (msgId: string) => {
  return resolve(
    doorClient.get<ImResponse<Conversation.Message>>('/oversea-conversation/v1/queryMessage', {
      params: {
        msgId,
      },
    }),
  );
};

export const getTtsMessageData = (params: {
  conversationId: string;
  msgId: string;
  isSbs?: boolean;
}) => {
  return resolve(
    doorClient.get<
      ImResponse<{
        data: string;
        message: Conversation.Message;
      }>
    >('/oversea-conversation/v1/getTtsMessageData', { params }),
  );
};

export interface TodoTypeData {
  tooMany: boolean;
  todoTypeNames: {
    type: number;
    typeName: string;
    count: number;
  }[];
}

export const getOpenTodoTypeName = (
  body: Pick<
    ApiConversation.ConversationListAdvanceParams,
    'follower' | 'region' | 'platform' | 'storeIds' | 'tunnelAccountIds'
  > & {
    onlyMy?: boolean;
  },
) => {
  return resolve(
    doorClient.post<ImResponse<TodoTypeData>>('/aggregation/v1/getOpenTodoTypeName', body),
  );
};

export const getTodoConversationList = (
  body: ApiConversation.ConversationListAdvanceParams & {
    onlyMy?: boolean;
  },
) => {
  return resolve(
    doorClient.post<
      ImResponse<{
        hasNext: boolean;
        nextOffset: string;
        todoTalkInfo: {
          talkInfo: Conversation.Conversation;
          todoId: string;
          todoType: number;
          todoTypeName: string;
        }[];
      }>
    >('/aggregation/v1/getTodoConversationList', body),
  ).then(({ todoTalkInfo, ...others }) => {
    return {
      ...others,
      todoTalkInfo: todoTalkInfo.map(({ talkInfo, ...others }) => ({
        ...talkInfo,
        ...others,
      })),
    };
  });
};

export interface ConversationTodoInfo {
  id: string;
  type: number;
  typeName: string;
}

export enum ImportantTodoScope {
  InvalidQueryScope = 'INVALID_QUERY_SCOPE',
  All = 'ALL',
  OnlyMy = 'ONLY_MY',
}

export const getImportantTodoType = () => {
  return resolve(
    doorClient.get<
      ImResponse<{
        todoTypeName: ConversationTodoInfo[];
        options: ConversationTodoInfo[];
        scope?: ImportantTodoScope;
      }>
    >('/overseaim-hi-manager/v1/getImportantTodoType'),
  );
};

export const updateImportantTodoType = (body: { type: number[]; scope?: ImportantTodoScope }) => {
  return doorClient.post('/overseaim-hi-manager/v1/updateImportantTodoType', body);
};

export const getImportantTodoConvCount = (
  body: Pick<
    ApiConversation.ConversationListAdvanceParams,
    'follower' | 'region' | 'platform' | 'storeIds' | 'tunnelAccountIds'
  > & {
    onlyMy?: boolean;
  },
) => {
  return resolve(
    doorClient.post<
      ImResponse<{
        allCount: number;
        unprocessedCount: number;
      }>
    >('/aggregation/v1/getImportantTodoConvCount', body),
  );
};

export const getHasImportantTodoUnprocessedConv = (
  body: Pick<
    ApiConversation.ConversationListAdvanceParams,
    'follower' | 'region' | 'platform' | 'storeIds' | 'tunnelAccountIds'
  > & {
    onlyMy?: boolean;
  },
) => {
  return resolve(
    doorClient.post<
      ImResponse<{
        has: boolean;
      }>
    >('/aggregation/v1/hasImportantTodoUnprocessedConv', body),
  );
};

interface AdvanceConversationListData {
  hasNext: boolean;
  nextOffset: string;
  aggTalks: {
    talk: Conversation.Conversation;
    phone: Conversation.TalkPhone;
    todoTypeName: ConversationTodoInfo;
  }[];
}

const transformConversationListData = ({ aggTalks, ...others }: AdvanceConversationListData) => {
  return {
    ...others,
    talkInfo: aggTalks.map(({ talk, phone, todoTypeName: { id, type, typeName } }) => ({
      ...extractFailCauseFromLastMsgContent(talk),
      phone,
      todoId: id === '0' ? undefined : id,
      todoType: id === '0' ? undefined : type,
      todoTypeName: id === '0' ? undefined : typeName,
    })),
  };
};

export const getImportantTodoConversationList = (
  body: ApiConversation.ConversationListAdvanceParams & {
    todoTypes: number[];
    unprocessed: boolean;
    onlyMy?: boolean;
  },
) => {
  return resolve(
    doorClient.post<ImResponse<AdvanceConversationListData>>(
      '/aggregation/v1/getImportantTodoConversationList',
      body,
    ),
  ).then(transformConversationListData);
};

export const advanceQueryConversationList = (
  body: ApiConversation.ConversationListAdvanceParams & { remind?: boolean },
) => {
  return resolve(
    doorClient.post<ImResponse<AdvanceConversationListData>>(
      '/aggregation/v1/advanceQueryConversationList',
      body,
    ),
  ).then(transformConversationListData);
};

/**
 * 批量获取会话信息
 */
export const getTalkInfoListWithPhone = (
  conversationIds: (number | string)[],
  signal?: AbortSignal,
) => {
  return resolve(
    doorClient
      .post<
        ImResponse<{
          aggTalks: Array<{
            talk: Conversation.Conversation;
            phone: Conversation.TalkPhone;
          }>;
        }>
      >(
        '/aggregation/v1/getAggTalkList',
        {
          conversationId: conversationIds,
        },
        { signal },
      )
      .then((response) => {
        if (response.data?.data?.aggTalks?.length > 0) {
          response.data.data.aggTalks = response.data.data.aggTalks.map(({ talk, phone }) => ({
            talk: extractFailCauseFromLastMsgContent(talk),
            phone,
          }));
        }
        return response;
      }),
  );
};
